<script setup>
import { ref, computed, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Button } from '@/Components/ui/button'
import { useTeacherDocumentUpload } from '@/composables/useTeacherDocumentUpload'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  originalName: {
    type: String,
    default: '',
  },
  fileSize: {
    type: Number,
    default: 0,
  },
  existingUrl: {
    type: String,
    default: null,
  },
  showError: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: 'Upload Document',
  },
  required: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'update:modelValue',
  'update:originalName',
  'update:fileSize',
  'upload:start',
  'upload:complete',
  'upload:error',
  'remove',
])

const { uploadDocument, isUploading, uploadProgress, uploadError: composableError } = useTeacherDocumentUpload()

const page = usePage()

const parseSizeToBytes = (size) => {
  const match = size.match(/^(\d+)\s*(MB|GB)$/i)
  if (!match) return 50 * 1024 * 1024
  const value = parseInt(match[1])
  const unit = match[2].toUpperCase()
  return unit === 'GB' ? value * 1024 * 1024 * 1024 : value * 1024 * 1024
}

const maxSizeConfig = computed(() => page.props.config?.mux?.content_document_max_size || '50MB')
const maxSizeBytes = computed(() => parseSizeToBytes(maxSizeConfig.value))

const fileInputRef = ref(null)
const localError = ref(null)
const displayName = ref(props.originalName || '')
const displaySize = ref(props.fileSize || 0)
const previewUrl = ref(props.existingUrl || null)

// Watch for external URL changes (e.g. when editing existing content)
watch(() => props.existingUrl, (newVal) => {
  if (newVal) previewUrl.value = newVal
})

watch(() => props.originalName, (newVal) => {
  if (newVal) displayName.value = newVal
})

const formatFileSize = (bytes) => {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const hasFile = computed(() => !!props.modelValue || !!previewUrl.value)

const triggerFileInput = () => {
  fileInputRef.value?.click()
}

const handleFileSelect = async (e) => {
  const file = e.target.files[0]
  if (!file) return

  // Validate file type
  if (file.type !== 'application/pdf') {
    localError.value = 'Only PDF files are allowed'
    return
  }

  // Validate file size
  if (file.size > maxSizeBytes.value) {
    localError.value = `File must be smaller than ${maxSizeConfig.value}`
    return
  }

  localError.value = null
  emit('upload:start')

  try {
    const result = await uploadDocument(file, 'content-material')

    emit('update:modelValue', result.path)
    emit('update:originalName', result.original_name)
    emit('update:fileSize', result.size)
    displayName.value = result.original_name
    displaySize.value = result.size

    // Create a local preview URL
    previewUrl.value = URL.createObjectURL(file)

    emit('upload:complete', result)
  } catch (error) {
    localError.value = error.message || 'Upload failed'
    emit('upload:error', error)
  }

  // Reset file input
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

const removeFile = () => {
  emit('update:modelValue', '')
  emit('update:originalName', '')
  emit('update:fileSize', 0)
  displayName.value = ''
  displaySize.value = 0
  previewUrl.value = null
  localError.value = null
  emit('remove')
}

const errorMessage = computed(() => localError.value || composableError.value)
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Error Message -->
    <div v-if="errorMessage" class="p-3 bg-red-50 border border-red-200 rounded-lg">
      <div class="flex items-start gap-2">
        <span class="material-symbols-outlined text-red-600 text-sm mt-0.5">error</span>
        <p class="text-sm text-red-700">{{ errorMessage }}</p>
      </div>
    </div>

    <!-- Upload Dropzone (when no file) -->
    <div
      v-if="!hasFile && !isUploading"
      @click="triggerFileInput"
      :class="{
        'border-red-500 bg-red-50': showError,
        'border-gray-300 bg-gray-50 hover:bg-blue-50/50 hover:border-blue-600/50': !showError,
      }"
      class="group relative flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-xl transition-all cursor-pointer"
    >
      <div class="flex flex-col items-center justify-center text-center px-4">
        <div class="mb-3 rounded-full bg-white p-3 shadow-sm group-hover:scale-110 transition-transform duration-300 text-blue-600">
          <span class="material-symbols-outlined text-2xl">upload_file</span>
        </div>
        <p class="mb-1 text-base font-medium text-gray-900">
          <span class="text-blue-600 hover:underline font-bold">Click to upload</span> or drag and drop
        </p>
        <p class="text-sm text-gray-500">PDF only (MAX. {{ maxSizeConfig }})</p>
      </div>
      <input
        ref="fileInputRef"
        @change="handleFileSelect"
        class="hidden"
        type="file"
        accept="application/pdf,.pdf"
      />
    </div>

    <!-- Uploading State -->
    <div
      v-if="isUploading"
      class="w-full h-48 border-2 border-gray-200 rounded-xl bg-gray-50 flex flex-col items-center justify-center p-6"
    >
      <span class="material-symbols-outlined text-2xl text-blue-600 mb-3 animate-spin">sync</span>
      <h3 class="text-base font-semibold text-gray-900 mb-3">Uploading Document...</h3>
      <div class="w-full max-w-md bg-gray-200 rounded-full h-2 mb-2">
        <div
          class="bg-blue-600 h-2 rounded-full transition-all duration-300"
          :style="`width: ${uploadProgress}%`"
        ></div>
      </div>
      <p class="text-sm text-gray-600">{{ Math.round(uploadProgress) }}%</p>
    </div>

    <!-- Uploaded File Display -->
    <div v-if="hasFile && !isUploading" class="flex flex-col gap-3">
      <!-- File info bar -->
      <div class="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
        <div class="flex items-center gap-3 min-w-0">
          <span class="material-symbols-outlined text-red-500 text-xl shrink-0">picture_as_pdf</span>
          <div class="min-w-0">
            <p class="text-sm font-medium text-gray-900 truncate">{{ displayName || 'document.pdf' }}</p>
            <p v-if="displaySize" class="text-xs text-gray-500">{{ formatFileSize(displaySize) }}</p>
          </div>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <Button type="button" variant="outline" size="sm" @click="triggerFileInput">
            <span class="material-symbols-outlined text-sm mr-1">swap_horiz</span>
            Replace
          </Button>
          <Button type="button" variant="ghost" size="sm" @click="removeFile" class="text-red-600 hover:text-red-700 hover:bg-red-50">
            <span class="material-symbols-outlined text-sm">close</span>
          </Button>
        </div>
        <input
          ref="fileInputRef"
          @change="handleFileSelect"
          class="hidden"
          type="file"
          accept="application/pdf,.pdf"
        />
      </div>

      <!-- PDF Preview (iframe) -->
      <div v-if="previewUrl" class="w-full h-[500px] border border-gray-200 rounded-xl overflow-hidden">
        <iframe
          :src="previewUrl"
          class="w-full h-full"
          frameborder="0"
        />
      </div>
    </div>

    <!-- Error slot -->
    <slot name="error"></slot>
  </div>
</template>
